"use client";

import { useCallback, useEffect, useMemo, useState, type ReactNode } from "react";
import {
  ChevronDown,
  Eye,
  Loader2,
  Palette,
  Save as SaveIcon,
} from "lucide-react";
import { toast } from "sonner";

import type { IBrandQuoteThemeConfig } from "@/@sdk/brands/brand-model";
import { iBrand } from "@/@sdk/brands/brand-model";
import { useSDKActionWithDeps } from "@/@sdk/sdk.hooks";
import { useBrandById, useBrands } from "@/hooks/use-brands";
import { Button } from "@/components/ui/button";
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from "@/components/ui/collapsible";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Label } from "@/components/ui/label";
import { ScrollArea } from "@/components/ui/scroll-area";
import { cn } from "@/lib/utils";
import { WorkspaceSectionHeader } from "../workspace-section-header";
import { getEffectiveQuoteThemeConfig } from "@/components/quotes/quotes-template/quote-theme/quote-theme-presets";
import { ColorSchemaPicker } from "./quote-theme/color-schema-picker";
import { ColorPalettePicker } from "./quote-theme/color-palette-picker";
import { FontSchemaPicker } from "./quote-theme/font-schema-picker";
import { FontGroupPicker } from "./quote-theme/font-group-picker";
import { UiStylePicker } from "./quote-theme/ui-style-picker";
import { LayoutConfigPicker } from "./quote-theme/layout-config-picker";
import { AdvancedThemeOptions } from "./quote-theme/advanced-theme-options";
import { QuoteThemePreview } from "./quote-theme/quote-theme-preview";

function prepareQuoteThemeConfigForSave(
  config: IBrandQuoteThemeConfig,
  brandPrimaryColor?: string
): IBrandQuoteThemeConfig {
  const primary =
    config.colorPalette.presetId === "brand-primary" && brandPrimaryColor
      ? brandPrimaryColor
      : config.colorPalette.primary;

  const bodyFont =
    config.fontSchema === "SINGLE_FONT"
      ? config.fontGroup.headingFont
      : config.fontGroup.bodyFont;

  return {
    ...config,
    colorPalette: {
      ...config.colorPalette,
      primary,
    },
    fontGroup: {
      ...config.fontGroup,
      bodyFont,
    },
  };
}

export interface QuoteThemeSectionContentProps {
  brandId: string;
  brand: iBrand | undefined;
  draftConfig: IBrandQuoteThemeConfig;
  previewBrand: iBrand | undefined;
  advancedOpen: boolean;
  onAdvancedOpenChange: (open: boolean) => void;
  onUpdateConfig: (patch: Partial<IBrandQuoteThemeConfig>) => void;
  showPreview?: boolean;
  layout?: "embedded" | "fullscreen";
  headerActions?: ReactNode;
}

export function QuoteThemeSectionContent({
  brand,
  draftConfig,
  previewBrand,
  advancedOpen,
  onAdvancedOpenChange,
  onUpdateConfig,
  showPreview = false,
  layout = "embedded",
  headerActions,
}: QuoteThemeSectionContentProps) {
  const isFullscreen = layout === "fullscreen";

  const pickers = (
    <div className="space-y-8">
      <section className="space-y-3">
        <Label className="text-base">Color schema</Label>
        <ColorSchemaPicker
          value={draftConfig.colorSchema}
          onChange={(colorSchema) => onUpdateConfig({ colorSchema })}
        />
      </section>

      <section className="space-y-3">
        <Label className="text-base">Colour palette</Label>
        <ColorPalettePicker
          colorSchema={draftConfig.colorSchema}
          value={draftConfig.colorPalette}
          brandPrimaryColor={brand?.primaryColor}
          onChange={(colorPalette) => onUpdateConfig({ colorPalette })}
        />
      </section>

      <section className="space-y-3">
        <Label className="text-base">Font schema</Label>
        <FontSchemaPicker
          value={draftConfig.fontSchema}
          onChange={(fontSchema) => onUpdateConfig({ fontSchema })}
        />
      </section>

      <section className="space-y-3">
        <Label className="text-base">Font group</Label>
        <FontGroupPicker
          fontSchema={draftConfig.fontSchema}
          value={draftConfig.fontGroup}
          onChange={(fontGroup) => onUpdateConfig({ fontGroup })}
        />
      </section>

      <section className="space-y-3">
        <Label className="text-base">UI style</Label>
        <UiStylePicker
          value={draftConfig.uiStyle}
          onChange={(uiStyle) => onUpdateConfig({ uiStyle })}
        />
      </section>

      <section className="space-y-3">
        <Label className="text-base">Layout</Label>
        <LayoutConfigPicker
          value={draftConfig.layoutConfig}
          onChange={(layoutConfig) => onUpdateConfig({ layoutConfig })}
        />
      </section>

      <Collapsible open={advancedOpen} onOpenChange={onAdvancedOpenChange}>
        <CollapsibleTrigger asChild>
          <Button variant="ghost" className="w-full justify-between px-0">
            <span className="text-sm font-medium">Advanced options</span>
            <ChevronDown
              className={`h-4 w-4 transition-transform ${
                advancedOpen ? "rotate-180" : ""
              }`}
            />
          </Button>
        </CollapsibleTrigger>
        <CollapsibleContent className="pt-4">
          <AdvancedThemeOptions
            value={draftConfig}
            onChange={onUpdateConfig}
          />
        </CollapsibleContent>
      </Collapsible>
    </div>
  );

  return (
    <div
      className={cn(
        "space-y-6",
        !isFullscreen && "px-4 pb-6",
        isFullscreen && "flex h-full min-h-0 flex-col gap-0 space-y-0"
      )}
    >
      {!isFullscreen ? (
        <WorkspaceSectionHeader
          icon={Palette}
          title="Quote Theme"
          description="Configure colours, fonts, visual style, and layout for customer-facing quote pages."
          actions={headerActions}
        />
      ) : null}

      <div
        className={cn(
          "gap-8",
          showPreview
            ? "grid min-h-0 flex-1 lg:grid-cols-[minmax(0,380px)_minmax(0,1fr)]"
            : "",
          isFullscreen && showPreview && "flex-1 overflow-hidden p-4 pt-0"
        )}
      >
        {isFullscreen && showPreview ? (
          <ScrollArea className="h-full pr-4">{pickers}</ScrollArea>
        ) : (
          pickers
        )}

        {showPreview ? (
          <div
            className={cn(
              isFullscreen
                ? "min-h-0 overflow-hidden"
                : "xl:sticky xl:top-4 xl:self-start"
            )}
          >
            <QuoteThemePreview
              brand={previewBrand}
              quoteThemeConfig={draftConfig}
              variant={isFullscreen ? "fullscreen" : "sidebar"}
            />
          </div>
        ) : null}
      </div>
    </div>
  );
}

export function QuoteThemeSection({ brandId }: { brandId: string }) {
  const brand = useBrandById(brandId);
  const { retry: retryBrands } = useBrands();
  const [advancedOpen, setAdvancedOpen] = useState(false);
  const [previewOpen, setPreviewOpen] = useState(false);

  const initialConfig = useMemo(
    () =>
      getEffectiveQuoteThemeConfig(
        brand?.quoteThemeConfig,
        brand?.primaryColor
      ),
    [brand?.quoteThemeConfig, brand?.primaryColor]
  );

  const [draftConfig, setDraftConfig] =
    useState<IBrandQuoteThemeConfig>(initialConfig);

  useEffect(() => {
    setDraftConfig(initialConfig);
  }, [initialConfig]);

  const { doAction: editBrand, isProcessing } = useSDKActionWithDeps(
    () => ({
      action: (sdk) => (edits: Partial<iBrand>) =>
        sdk.brands.editById(brandId, edits),
      failureMessage: "Failed to update quote theme",
    }),
    [brandId]
  );

  const updateConfig = useCallback(
    (patch: Partial<IBrandQuoteThemeConfig>) => {
      setDraftConfig((current) => {
        const next = { ...current, ...patch };

        if (patch.fontSchema === "SINGLE_FONT") {
          next.fontGroup = {
            ...next.fontGroup,
            bodyFont: next.fontGroup.headingFont,
          };
        }

        return next;
      });
    },
    []
  );

  const handleSave = useCallback(async () => {
    try {
      const quoteThemeConfig = prepareQuoteThemeConfigForSave(
        draftConfig,
        brand?.primaryColor
      );
      await editBrand({ quoteThemeConfig });
      await retryBrands();
      toast.success("Quote theme saved");
    } catch {
      toast.error("Failed to update quote theme");
    }
  }, [brand?.primaryColor, draftConfig, editBrand, retryBrands]);

  const previewBrand = useMemo(
    (): iBrand | undefined =>
      brand
        ? {
            ...brand,
            quoteThemeConfig: draftConfig,
          }
        : undefined,
    [brand, draftConfig]
  );

  const sharedContentProps: Omit<
    QuoteThemeSectionContentProps,
    "showPreview" | "layout" | "headerActions"
  > = {
    brandId,
    brand,
    draftConfig,
    previewBrand,
    advancedOpen,
    onAdvancedOpenChange: setAdvancedOpen,
    onUpdateConfig: updateConfig,
  };

  const saveButton = (
    <Button onClick={handleSave} disabled={isProcessing || !brandId}>
      {isProcessing ? (
        <>
          <Loader2 className="mr-2 h-4 w-4 animate-spin" />
          Saving...
        </>
      ) : (
        <>
          <SaveIcon className="mr-2 h-4 w-4" />
          Save theme
        </>
      )}
    </Button>
  );

  const headerActions = (
    <div className="flex items-center gap-2">
      <Button variant="outline" onClick={() => setPreviewOpen(true)}>
        <Eye className="mr-2 h-4 w-4" />
        Preview
      </Button>
      {saveButton}
    </div>
  );

  return (
    <>
      <QuoteThemeSectionContent
        {...sharedContentProps}
        showPreview={false}
        layout="embedded"
        headerActions={headerActions}
      />

      <Dialog open={previewOpen} onOpenChange={setPreviewOpen}>
        <DialogContent className="fixed inset-0 flex h-screen w-screen max-h-none max-w-none translate-x-0 translate-y-0 flex-col gap-0 rounded-none p-0">
          <DialogHeader className="shrink-0 flex-row items-center justify-between space-y-0 border-b px-6 py-4">
            <DialogTitle>Quote theme preview</DialogTitle>
            <div className="flex items-center gap-2 pr-8">{saveButton}</div>
          </DialogHeader>
          <div className="min-h-0 flex-1 overflow-hidden">
            <QuoteThemeSectionContent
              {...sharedContentProps}
              showPreview
              layout="fullscreen"
            />
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
}
